(ns manatee.admin.task-config.form-components
  (:require [clojure.string :as s]
            [manatee.domain.task :as task-domain]
            [manatee.spec.core :as spec]
            [manatee.ui.subs :as ui-subs]
            [re-frame.core :as rf]
            [rothko.form2.checkbox.component :as checkbox]
            [rothko.form2.component :as form]
            [rothko.form2.input.component :as input]
            [rothko.form2.label.component :as label]
            [rothko.form2.select.core :as select]
            [rothko.form2.validation :as validation]))

(defn data->account-id-key
  [{:keys [account-id]}]
  (let [{:keys [account-ids]} @(rf/subscribe [::ui-subs/config])]
    (-> (filter (comp #{account-id} account-ids) (keys account-ids))
        first)))

(defn data->type-option-key
  [task-config-data]
  (let [answer-fields (get-in task-config-data [:answer :fields])]
    (-> (filter (fn [v] (and (:field-name v) (:field-type v))) answer-fields)
        first
        :field-type
        keyword)))

(defn data->part-score-type-key
  [{:keys [part]}]
  (let [fields (:fields part)
        key (-> (filter (fn [v] (and (:field-name v)
                                     (= :drop-down (:field-type v)))) fields)
                first
                :field-name
                keyword)]
    (if key key :none)))

(defn data->answer-score-type-key
  [{:keys [answer]}]
  (let [fields (:fields answer)
        init-value (-> (filter (fn [v] (and (:field-name v)
                                            (= :drop-down (:field-type v)))) fields)
                       first
                       :field-name
                       keyword)]
    (if (= :cefr-level init-value) init-value
        :none)))

(defn data->assessment-name-key
  [{:keys [assessment-name]}]
  (keyword assessment-name))

(defn data->scoring-criteria
  [data]
  (let [fields (get-in data [:answer :fields])
        criteria-fields (filter (fn [v] (:criteria v)) fields)]
    (-> criteria-fields
        first
        :criteria)))

(defn data->scoring-criteria-keys
  [data options]
  (let [task-config-value (data->scoring-criteria data)
        raw-values (for [option (map :value options)
                         criterion task-config-value
                         :when (= (dissoc criterion :label)
                                  (dissoc option :label))]
                     option)]
    raw-values))

(defn score-range-from-to
  [task-config]
  (-> (task-domain/field-by-type task-config :answer #{:scoring})
      :score-range
      ;; Our UI is simpler than the data which has the full range,
      ;; not just the from/to numbers (because there is no need at the moment
      ;; for a more complex UI), so this is an approximation, but it should
      ;; be okay, as the value is read-only in the edit task UI.
      ((juxt first last))))

(defn reasons->select-keys
  [task-config-value]
  (for [{:keys [label value]} task-config-value]
    {:label (or label (name value)) :value (keyword value)}))

(defn data->part-score-type-keys
  [{:keys [part]}]
  (let [reasons (get-in part [:rejections :reasons])]
    (reasons->select-keys reasons)))

(defn data->answer-score-type-keys
  [{:keys [answer]}]
  (let [reasons (get-in answer [:rejections :reasons])]
    (reasons->select-keys reasons)))

(defn keywords->options
  [keywords all-options]
  (for [key keywords]
    (let [[{:keys [label]}] (filter (fn [{:keys [value]}] (= value key)) all-options)]
      {:label (or label (name key)) :value (keyword key)})))

(defn types->options
  [types]
  (for [{:keys [id label]} types]
    {:value id
     :representation label}))

(defn format-score-label
  [{:keys [label score-range]}]
  (str
   (first (s/split label #" \("))
   (when score-range
     (str " (" (first score-range) "-" (last score-range) ")"))))

(defn domain-options->form-options
  [options]
  (map
   (fn [option]
     (-> option
         (assoc :value option
                :representation (format-score-label option))))
   options))

(defn task-name-good?
  [existing-task-names]
  (fn [_form field]
    (cond (or (not (string? field)) (s/blank? field)) [:blank]
          (> (count field) 50) [:too-long]
          (contains? existing-task-names field) [:name-clash]
          :else ::validation/success)))

(defn scoring-criteria-options
  []
  (-> (concat
       task-domain/speaking-scoring-criteria
       task-domain/ceq-scoring-criteria)
      (domain-options->form-options)))

(def task-components
  "Map task component name to a function that, when called, returns the
   corresponding component (without the `::form/name` field)."
  {:task-id
   (fn task-id
     [_props
      {:keys [id] :as _form-values}]
     {::form/initial-value id
      ::input/type :hidden})

   :task-name
   (fn task-name
     [{:keys [existing-task-names] :as _props}
      {:keys [task-name] :as _form-values}]
     (let [messages {:blank "Must not be empty"
                     :too-long "Must be up to 50 characters"
                     :name-clash "Must not be the same as an existing task"}]
       {:appearance #{:primary :task-page__name}
        ::form/label "Task name"
        ::form/initial-value task-name
        ::validation/fn (task-name-good? (disj existing-task-names task-name))
        ::validation/messages messages
        ::input/type :text}))

   :team-id (fn team-id
              [{:keys [user-teams project-owner?] :as _props} {:keys [team-id] :as _form-values}]
              (let [appearance (cond-> #{:primary}
                                 project-owner? (conj :admin-page__hidden-form))]
                {:appearance appearance
                 ::form/label "Team"
                 ::select/options user-teams
                 ::form/initial-value (or team-id :no-team)
                 ::form/disabled? project-owner?}))

   :source
   (fn source
     [{:keys [sources existing-task?] :as _props}
      {:keys [source-value] :as _form-values}]
     (let [options (for [{:keys [id label]} sources]
                     {:value id
                      :representation label})]
       {:appearance #{:primary}
        ::form/label "Source"
        ::form/placeholder "Choose source..."
        ::form/initial-value source-value
        ::form/disabled? existing-task?
        ::validation/spec ::spec/some?
        ::validation/messages {::spec/some* "Must not be empty"}
        ::select/options options}))

   :field-type
   (fn field-type
     [{:keys [types existing-task?] :as _props}
      {:keys [type-value] :as _form-values}]
     (let [options (for [{:keys [id label]} types]
                     {:value id
                      :representation label})]
       {:appearance #{:primary}
        ::form/label "Type"
        ::form/placeholder "Choose type..."
        ::form/initial-value type-value
        ::form/disabled? existing-task?
        ::validation/spec ::spec/some?
        ::validation/messages {::spec/some* "Must not be empty"}
        ::select/options options}))

   :part-score-type
   (fn part-score-type
     [{:keys [part-score-types existing-task?] :as _props}
      {:keys [part-score-type-value] :as _form-values}]
     {:appearance #{:primary}
      ::form/label "Part score type"
      ::form/placeholder "Choose part score type..."
      ::form/initial-value part-score-type-value
      ::form/disabled? (fn [{:keys [::form/value]} _] (or existing-task? (:suppress-part-screen? value)))
      ::select/options (types->options part-score-types)
      ::validation/spec ::spec/some?
      ::validation/messages {::spec/some* "Must not be empty"}})

   :answer-score-type
   (fn answer-score-type
     [{:keys [answer-score-types existing-task?] :as _props}
      {:keys [answer-score-type-value] :as _form-values}]
     {:appearance #{:primary}
      ::form/label "Answer score type"
      ::form/placeholder "Choose answer score type..."
      ::form/initial-value answer-score-type-value
      ::form/disabled? existing-task?
      ::select/options (types->options answer-score-types)
      ::validation/spec ::spec/some?
      ::validation/messages {::spec/some* "Must not be empty"}})

   :scoring-criteria
   (fn scoring-criteria
     [{:keys [existing-task?] :as _props}
      {:keys [scoring-criteria-value] :as _form-values}]
     {::form/label "Scoring criteria"
      ::form/placeholder "Choose scoring criteria..."
      ::form/initial-value scoring-criteria-value
      ::form/disabled? existing-task?
      ::select/options (scoring-criteria-options)
      :class [:task-page__user-selector]
      :appearance #{:primary :pills-inside-input}})

   :score-range-from
   (fn score-range-from
     [_props
      {:keys [score-range] :as _form-values}]
     {:appearance #{:primary}
      ::form/label "Score range"
      ::form/initial-value (:from score-range)
      ::form/disabled? true
      ::input/type :number
      ::validation/spec ::spec/score-range-int
      ::validation/messages {::spec/score-range-int spec/score-range-int-message}})

   :score-range-to
   (fn score-range-to
     [_props
      {:keys [score-range] :as _form-values}]
     {:appearance #{:primary}
      ::form/label "\u00A0"
      ::form/initial-value (:to score-range)
      ::form/disabled? true
      ::input/type :number
      ::validation/spec ::spec/score-range-int
      ::validation/messages {::spec/score-range-int spec/score-range-int-message}})

   :part-rejection-options-speech
   (fn part-rejection-options-speech
     [{:keys [part-rejection-options-speech] :as _props}
      {:keys [part-rejection-value] :as _form-values}]
     (let [disabled? (fn [{:keys [::form/value]} _] (and (empty? part-rejection-value)
                                                         (:suppress-part-screen? value)))]
       {::form/label "Part rejection options"
        ::form/placeholder "Choose part rejection options..."
        ::form/initial-value part-rejection-value
        ::form/disabled? disabled?
        ::select/options (domain-options->form-options part-rejection-options-speech)
        :class [:task-page__user-selector]
        :appearance #{:primary :pills-inside-input}}))

   :part-rejection-options-text
   (fn part-rejection-options-text
     [{:keys [part-rejection-options-text] :as _props}
      {:keys [part-rejection-value] :as _form-values}]
     (let [disabled? (fn [{:keys [::form/value]} _] (and (empty? part-rejection-value)
                                                         (:suppress-part-screen? value)))]
       {::form/label "Part rejection options"
        ::form/placeholder "Choose part rejection options..."
        ::form/initial-value part-rejection-value
        ::form/disabled? disabled?
        ::select/options (domain-options->form-options part-rejection-options-text)
        :class [:task-page__user-selector]
        :appearance #{:primary :pills-inside-input}}))

   :answer-rejection-options-speech
   (fn answer-rejection-options-speech
     [{:keys [answer-rejection-options-speech] :as _props}
      {:keys [answer-rejection-value] :as _form-values}]
     {::form/label "Answer rejection options"
      ::form/placeholder "Choose answer rejection options..."
      ::form/initial-value answer-rejection-value
      ::select/options (domain-options->form-options answer-rejection-options-speech)
      :class [:task-page__user-selector]
      :appearance #{:primary :pills-inside-input}})

   :answer-rejection-options-text
   (fn answer-rejection-options-text
     [{:keys [answer-rejection-options-text] :as _props}
      {:keys [answer-rejection-value] :as _form-values}]
     {::form/label "Answer rejection options"
      ::form/placeholder "Choose answer rejection options..."
      ::form/initial-value answer-rejection-value
      ::select/options (domain-options->form-options answer-rejection-options-text)
      :class [:task-page__user-selector]
      :appearance #{:primary :pills-inside-input}})

   :first-stage-rejections
   (fn first-stage-rejections
     [_props
      {:keys [first-stage-rejections-value] :as _form-values}]
     {::form/label "First stage rejections"
      ::form/placeholder "Choose which first stage rejections to use..."
      ::form/initial-value first-stage-rejections-value
      ::select/options (domain-options->form-options task-domain/first-stage-rejections-values)
      :class [:task-page__user-selector]
      :appearance #{:primary :pills-inside-input}})

   :next-stage-part-rejections
   (fn next-stage-part-rejections
     [_props
      {:keys [next-stage-part-rejections-value] :as _form-values}]
     {::form/label "Next stage part rejections"
      ::form/placeholder "Choose which part rejections to use..."
      ::form/initial-value next-stage-part-rejections-value
      ::select/options (domain-options->form-options task-domain/next-stage-part-rejections-values)
      :class [:task-page__user-selector]
      :appearance #{:primary :pills-inside-input}})

   :next-stage-answer-rejections
   (fn next-stage-answer-rejections
     [_props
      {:keys [next-stage-answer-rejections-value] :as _form-values}]
     {::form/label "Next stage answer rejections"
      ::form/placeholder "Choose which answer rejections to use..."
      ::form/initial-value next-stage-answer-rejections-value
      ::select/options (domain-options->form-options task-domain/next-stage-answer-rejections-values)
      :class [:task-page__user-selector]
      :appearance #{:primary :pills-inside-input}})

   :custom-validation-rules
   (fn custom-validation-rules
     [_props
      {:keys [custom-validation-rules-value] :as _form-values}]
     {::form/label "Custom validation rules"
      ::form/placeholder "Choose validation rules..."
      ::form/initial-value custom-validation-rules-value
      ::select/options (domain-options->form-options task-domain/custom-validation-rules)
      :class [:task-page__user-selector]
      :appearance #{:primary :pills-inside-input}})

   :assessment-name
   (fn assessment-name
     [_props
      {:keys [assessment-name-value] :as _form-values}]
     {:appearance #{:primary}
      ::form/label "Assessment name"
      ::form/placeholder "Choose assessment name..."
      ::form/initial-value assessment-name-value
      ::select/options task-domain/assessments})})

(def modifier-components
  "Map task modifier component name to a function that, when called, returns
   the corresponding component (without the `::form/name` field)."
  {:multi-marking?
   (fn multi-marking?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Multi-marking?"
      :tooltip "More than 1 user will mark all items."})

   :multi-reject?
   (fn multi-reject?
     [initial-value]
     {:appearance #{:add-task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Multi-reject?"
      :tooltip "More than 1 rejection reason may be given."})

   :suppress-part-screen?
   (fn suppress-part-screen?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Suppress part screen?"
      :tooltip "Use only if all items are unrelated to each other, i.e. no parts, as in CEQs and Linguaskill."})

   :avoid-rejected-items?
   (fn avoid-rejected-items?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Avoid rejected items?"
      :tooltip "Multi-marking only. If one examiner rejects an item, do not send it to other examiners for marking."})

   :allow-reports?
   (fn allow-reports?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Allow reports?"
      :tooltip "User can report an item to an ELiT admin for advice or to notify of an issue."})

   :allow-deferments?
   (fn allow-deferments?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Allow deferments?"
      :tooltip "User can send an item back to their queue to be annotated at a later point."})

   :allow-going-back?
   (fn allow-going-back?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Allow going back?"
      :tooltip "User can go back to their previous annotation to amend it."})

   :allow-notes?
   (fn allow-notes?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Allow user notes?"
      :tooltip "User can attach notes to edits."})

   :reject-with-annotation?
   (fn reject-with-annotation?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Reject with annotation?"
      :tooltip "Ask annotators to provide annotation (scores) when submitting rejection."})

   :show-short-rubric?
   (fn show-short-rubric?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Show question description?"
      :tooltip "Show a short rubric (e.g. Interview, as in Linguaskill Part 1)."})

   :show-long-rubric?
   (fn show-long-rubric?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Show long question description?"
      :tooltip "Show an extensive rubric if available."})

   :show-ai-hybrid?
   (fn show-ai-hybrid?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Show answer ai-hybrid checkbox?"
      :tooltip "The AI-Hybrid checkbox will appear in annotation."})

   :show-analytic-scores?
   (fn show-analytic-scores?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Show analytic scores?"
      :tooltip "Show analytic scores if available."})

   :hide-media?
   (fn hide-media?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Hide media?"
      :tooltip "Hide answer audio, e.g. in Stage 3 speech annotation."})

   :archive-task?
   (fn archive-task?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Archive task?"
      :tooltip "Hide this task in task lists."})

   :suppress-first-iterations?
   (fn suppress-first-iterations?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Suppress first iterations?"
      :tooltip "Suppress first iterations for text submissions."})

   :show-answers-remaining?
   (fn show-answers-remaining?
     [initial-value]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::checkbox/option true
      ::checkbox/label "Show answer count remaining?"
      :tooltip "The number of answers remaining will show in the dashboard."})

   :released-submissions-only?
   (fn released-submissions-only?
     [initial-value disabled?]
     {:appearance #{:task-page__checkbox-spacing}
      ::form/initial-value initial-value
      ::form/disabled? disabled?
      ::checkbox/option true
      ::checkbox/label "Released submissions only?"
      :tooltip "Limit the dataset to submissions indicated in the released_submissions table for the given task config."})})

(defn upload-button
  [!atom file-ext]
  [:span.upload-label
   [:label
    [:input.hidden-xs-up
     {:type "file" :id :file :accept file-ext
      :on-change #(reset! !atom (some-> % .-target .-files (aget 0)))}]
    [:i.fa.fa-upload.fa-lg]]])

(defn upload-button-div
  [!uploaded-submissions]
  [:div {:class [:task-page__file-input]}
   [label/component {::form/name :file, ::form/label "Upload submissions (CSV, JSON Lines or EDN file)"}]
   [upload-button !uploaded-submissions ".csv,.json,.jsonl,.edn"]])
